export type IncidentService = "auth" | "database" | "provider" | "templates";
export type IncidentSnapshot = {
  unavailable: IncidentService[];
  backendUnavailable: boolean;
};

const services: IncidentService[] = ["auth", "database", "provider", "templates"];
const isIncidentService = (value: unknown): value is IncidentService =>
  typeof value === "string" && services.includes(value as IncidentService);

type MonitorOptions = {
  supabaseUrl: string;
  apiKey: string;
  siteOrigin: string;
  isOnline: () => boolean;
  isVisible: () => boolean;
  fetchImpl?: typeof fetch;
  timeoutSignal?: (milliseconds: number) => AbortSignal;
};

export class ServiceIncidentMonitor {
  private failures = 0;
  private state: IncidentSnapshot = { unavailable: [], backendUnavailable: false };

  constructor(private readonly options: MonitorOptions) {}

  snapshot(): IncidentSnapshot {
    return { unavailable: [...this.state.unavailable], backendUnavailable: this.state.backendUnavailable };
  }

  pause(): IncidentSnapshot {
    this.failures = 0;
    this.state = { ...this.state, backendUnavailable: false };
    return this.snapshot();
  }

  private eligible(): boolean {
    return this.options.isOnline() && this.options.isVisible();
  }

  private signal(parent: AbortSignal, milliseconds: number): AbortSignal {
    return AbortSignal.any([parent, (this.options.timeoutSignal ?? AbortSignal.timeout)(milliseconds)]);
  }

  private async siteReachable(parent: AbortSignal): Promise<boolean> {
    try {
      const response = await (this.options.fetchImpl ?? fetch)(`${this.options.siteOrigin}/robots.txt`, {
        cache: "no-store",
        credentials: "omit",
        redirect: "manual",
        referrerPolicy: "no-referrer",
        signal: this.signal(parent, 3000),
      });
      if (!response.ok || response.headers.get("content-type")?.split(";", 1)[0]?.trim().toLowerCase() !== "text/plain") return false;
      const body = await response.text();
      return body.length <= 4096 && /^User-agent:/im.test(body) && this.eligible() && !parent.aborted;
    } catch {
      return false;
    }
  }

  private async recordFailure(parent: AbortSignal): Promise<IncidentSnapshot> {
    if (!this.eligible() || parent.aborted) return this.pause();
    this.failures += 1;
    if (this.failures < 2) return this.snapshot();
    if (this.state.backendUnavailable) return this.snapshot();
    if (await this.siteReachable(parent)) {
      this.state = { ...this.state, backendUnavailable: true };
    } else {
      this.failures = 0;
      this.state = { ...this.state, backendUnavailable: false };
    }
    return this.snapshot();
  }

  async poll(parent: AbortSignal): Promise<IncidentSnapshot> {
    if (!this.eligible() || parent.aborted) return this.pause();
    try {
      const response = await (this.options.fetchImpl ?? fetch)(`${this.options.supabaseUrl}/functions/v1/service-status`, {
        headers: {
          apikey: this.options.apiKey,
          Authorization: `Bearer ${this.options.apiKey}`,
        },
        cache: "no-store",
        redirect: "manual",
        signal: this.signal(parent, 5000),
      });
      if (parent.aborted || !this.eligible()) return this.pause();
      if (response.status === 401 || response.status === 403) return this.pause();
      if (response.status >= 500) return this.recordFailure(parent);
      if (!response.ok) return this.pause();
      let payload: unknown;
      try {
        payload = await response.json();
      } catch {
        // A malformed response is not evidence that the whole backend is down.
        this.failures = 0;
        return this.snapshot();
      }
      if (parent.aborted || !this.eligible()) return this.pause();
      const rows = payload && typeof payload === "object" && "services" in payload
        ? (payload as { services: unknown }).services : null;
      if (!Array.isArray(rows) || rows.length !== services.length ||
        new Set(rows.map((row) => row?.service)).size !== services.length ||
        !rows.every((row) => isIncidentService(row?.service) &&
          (row.status === "unavailable" || row.status === "unknown"))) {
        this.failures = 0;
        return this.snapshot();
      }
      this.failures = 0;
      this.state = {
        unavailable: rows.filter((row) => row.status === "unavailable").map((row) => row.service),
        backendUnavailable: false,
      };
      return this.snapshot();
    } catch {
      if (parent.aborted) return this.snapshot();
      return this.recordFailure(parent);
    }
  }
}
